import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { canPerformApiAction, type Session } from '../session.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { POSTAL_ADDRESS_TEMPLATES } from '../email-templates/templates.js';
import { useContentMap } from '../content.js';

/**
 * SCRN 23 — Personal details: View, amend and delete postal address — CCF-30
 * (view & delete) + CCF-31 (display & add/update for a vulnerable customer).
 *
 * Faithful port of the customer's Figma "update/delete postal address" design
 * onto the Accounts-domain postal-address gateway contract. The agent picks a
 * non-mortgage account (label via the shared `accountOptionLabel` helper —
 * `<product name> <last 4>`, issue #802, matching every other servicing
 * dropdown; only the last 4 digits ever show per CCF-80); the screen shows the postal
 * address on file (Lines 1–5 — 1–2 mandatory — and the Reason dropdown), with
 * "Delete details" (CCF-30) and "Update details" (CCF-31, add/amend). All fields
 * are validated to the CCF-31 rules (max 40 chars, allowed character set, Lines
 * 1–2 + reason mandatory). On delete the fields clear and a confirmation shows;
 * on update a confirmation shows. Amend (Update) is RBAC-gated per role/state:
 * ServiceAgent is view + delete only while Online (#735), and restricted roles
 * are blocked Offline (#248), with the CCF-31 VCL exception for ECLAgent (#285).
 *
 * Live data via the Teller-ID-gated BFF through the generated typed client:
 *   - accounts: POST /api/customers/accounts
 *   - view:     POST /api/accounts/postal-address
 *   - update:   POST /api/accounts/postal-address-update
 *   - delete:   POST /api/accounts/postal-address-delete
 *
 * The shared agent banner ("NavDark") lives in the app shell (AppLayout), so this
 * screen renders only the `<main>` content.
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
}

interface PostalAddress {
  Found?: boolean;
  AccountNumber?: string;
  Line1?: string;
  Line2?: string;
  Line3?: string;
  Line4?: string;
  Line5?: string;
  Reason?: string;
}

/** Reason-for-postal-address options (CCF-31). */
const REASONS = ['Fair Deal Scheme', 'Customer Request', 'Internal Process'];

/**
 * Issue #104 — build the inline failure text for the postal-address Update
 * action. Surfaces the gateway's own Code + Message verbatim (e.g.
 * "1450 — Validation of Input Payload has failed") so the agent sees the real
 * decline reason rather than a bare generic toast. Tolerates BOTH outcome
 * shapes the BFF can return:
 *
 *  - a 200 with `Success:false` carrying `ResultCode`/`ResultMessage` (the
 *    gateway's SuccessErrorInd:'N' business-error, mapped by `mapWriteOutcome`);
 *  - a 4xx `gateway_error` body `{ gateway: { code, message } }` (a true HTTP
 *    rejection — e.g. the real staging gateway's JWS-wrapped 400 — now decoded
 *    by the BFF, issue #104).
 *
 * `0000` is the internal success code and is never shown. Falls back to a
 * generic line only when the gateway gave neither (e.g. a transport failure).
 */
export function formatPostalUpdateError(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  const showCode = code && code !== '0000';
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Postal address update failed (code ${code}).`;
  return 'Could not update the postal address. Please retry.';
}

/**
 * Issue #138 — the same Code + Message surfacing as `formatPostalUpdateError`,
 * but for the postal-address Delete action (so the agent sees the gateway's real
 * decline reason inline beside the Delete button, recoverable + retryable, rather
 * than a vague toast). Tolerates both the 4xx `gateway_error` body and the 200
 * `Success:false` (`ResultCode`/`ResultMessage`) outcome. Only the fallback
 * wording differs.
 */
export function formatPostalDeleteError(body: unknown): string {
  const b = (body ?? {}) as {
    ResultCode?: string;
    ResultMessage?: string;
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = b.gateway?.code ?? b.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (b.gateway?.message ?? b.ResultMessage)?.trim();
  const showCode = code && code !== '0000';
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Postal address deletion failed (code ${code}).`;
  return 'Could not delete the postal address. Please retry.';
}

/**
 * Postal-line character policy — issues #104, #446, #512.
 *
 * We enforce EXACTLY the CCF-31 permitted character set on each postal-address
 * line, client-side (up-front) AND in the BFF: A–Z, a–z, 0–9, apostrophe (`'`),
 * hyphen (`-`), comma (`,`), full stop (`.`) and space. Anything outside that set
 * (e.g. `@`, `&`, `(`, `)`, `;`, `/`, `#`, `:`) is blocked before submit with a
 * clear inline error (the #80/#82 recoverable-error UX).
 *
 * Reconciliation of the flip-flop history (do NOT reverse either ruling):
 *  - Issue #104 originally blocked comma (`,`), apostrophe (`'`), ampersand
 *    (`&`), parentheses (`(` `)`) and semicolon (`;`) up-front because the real
 *    PTSB gateway op `address/modify` 1430-rejects them.
 *  - Issue #446 (@dylan219: "Go with option 2 please and allow as per the CCF,
 *    we will raise to GW then to fix") REVERSED that and required the app to
 *    allow the CCF set — notably comma and apostrophe — leaving the gateway to
 *    reject anything it doesn't yet accept (its `1430` Code/Message still
 *    surfaces inline). But that reversal removed ALL character-set validation,
 *    so genuinely non-CCF characters such as `@` slipped through with no error
 *    (issue #512).
 *  - Issue #512 restores the character-set check to EXACTLY the CCF-31 set. This
 *    still PERMITS comma and apostrophe (honouring #446 — they are inside the
 *    allowed set) and only blocks characters that are not in the CCF at all
 *    (e.g. `@`). If the (still strict) gateway rejects an allowed-but-not-yet-
 *    accepted character on submit, its exact `1430` Code/Message continues to
 *    surface inline via `formatPostalUpdateError`.
 *
 * This regex mirrors SCRN-24's `ADDRESS_LINE_ALLOWED` (PersonalDetailsScreen)
 * so both address flows enforce the same CCF-31 set.
 */
const ADDRESS_LINE_ALLOWED = /^[A-Za-z0-9'\-,. ]*$/;

/** The shared "allowed characters" wording for the inline / server errors (#512). */
const ADDRESS_LINE_CHARSET_MESSAGE =
  "can only contain letters, numbers, apostrophe ('), hyphen (-), comma (,), full stop (.) and spaces.";

/** Validates a postal line (CCF-31 / issues #104, #446, #512). Returns an error or '' when valid. */
function validateLine(label: string, value: string, required: boolean): string {
  if (!value) return required ? `${label} is required.` : '';
  if (value.length > 40) return `${label} must be 40 characters or fewer.`;
  if (!ADDRESS_LINE_ALLOWED.test(value))
    return `${label} ${ADDRESS_LINE_CHARSET_MESSAGE}`;
  return '';
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between disabled:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:hover:border-[#dad7d3]"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A labelled text input with an inline error slot. */
function Field({
  id,
  label,
  value,
  onChange,
  error,
  readOnly = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  readOnly?: boolean;
}): React.JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={40}
        readOnly={readOnly}
        aria-invalid={!!error}
        aria-readonly={readOnly}
        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] read-only:bg-[#f3f4f6] read-only:cursor-not-allowed read-only:focus:ring-0 read-only:focus:border-[#979c9f] ${
          error ? 'border-[#dc2626]' : 'border-[#979c9f]'
        }`}
      />
      <div className="min-h-[18px]">
        {error && (
          <p
            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

export function PostalAddressScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const contentMap = useContentMap();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Editable postal-address fields (CCF-30/31).
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [line3, setLine3] = useState('');
  const [line4, setLine4] = useState('');
  const [line5, setLine5] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Issue #104 — persistent inline outcome beside the action buttons (a toast
  // alone auto-dismisses and is easy to miss). `updateError` carries the
  // gateway's Code + Message verbatim on a failed update (recoverable — the
  // agent can correct + retry); `updateOk` is the definite success confirmation
  // (#82: a toast alone left it ambiguous whether the change actually applied).
  const [updateError, setUpdateError] = useState('');
  const [updateOk, setUpdateOk] = useState(false);
  // Issue #138 — the Delete action previously gave only a self-dismissing toast
  // (no confirmation beside the button), so the agent had no durable signal the
  // delete actually succeeded. Mirror the Update action's pattern: `deleteError`
  // carries the gateway's Code + Message verbatim on a failed delete
  // (recoverable — the agent can retry), `deleteOk` is the definite success
  // confirmation shown inline beside the Delete button.
  const [deleteError, setDeleteError] = useState('');
  const [deleteOk, setDeleteOk] = useState(false);

  // Issue #399 — the current session's Online/Offline state + per-action write
  // grants, so the screen renders the address VIEW-ONLY up-front when the agent
  // cannot mutate it in the current interaction state (rather than letting them
  // type and fail at submit with a 403). This is keyed off the ACTUAL per-state
  // permission for the postal-address write actions, NOT a blanket "offline"
  // check — so it automatically keeps the CCF-31 VCL exception editable
  // (ECLAgent offline, plus the full-offline supervisory roles, #285) and blocks
  // the restricted roles offline (#248), with zero special-casing. Deny-by-
  // default until the session resolves; the server-side RBAC gate
  // (`requirePermission`) stays the authoritative enforcement point regardless.
  //
  // Issue #735 (@dylan219): Update and Delete are gated INDEPENDENTLY —
  // ServiceAgent is view + delete only while Online (edit removed in every
  // state), so coupling the two behind one flag would wrongly hide the still-
  // permitted Delete. `updateBlockedByRole` distinguishes a ROLE-level update
  // denial (no state would permit it — #735) from a STATE-only one (e.g. a
  // restricted role on an offline journey, #248), so a role-level denial shows
  // @dylan219's verbatim permission copy rather than the #399 online-interaction
  // hint (same discrimination as the #727 reactive 403 path).
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [updateBlockedByRole, setUpdateBlockedByRole] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/session');
        if (cancelled || !data) return;
        const session = data as unknown as Session;
        setCanUpdate(canPerformApiAction(session, 'updatePostalAddress'));
        setCanDelete(canPerformApiAction(session, 'deletePostalAddress'));
        // Role-level denial: the update grant is absent, or denies in BOTH
        // states — no Online transition can make this agent permitted.
        const updateGrant = session.permissions.find(
          (p) => p.kind === 'api' && p.id === 'updatePostalAddress',
        );
        setUpdateBlockedByRole(
          !updateGrant || (!updateGrant.online && !updateGrant.offline),
        );
      } catch {
        // Fail-safe: on any error keep the read-only defaults. The server still
        // enforces the write gates, so a session-fetch hiccup never enables an
        // action the agent isn't actually permitted to perform.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the customer's accounts; the selector shows non-mortgage accounts.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // Non-mortgage accounts only (CCF-30/31) — loan/mortgage accounts excluded.
  const nonMortgageAccounts = accounts.filter((a) => a.AccountType !== 'LOAN');

  function fillFrom(addr: PostalAddress): void {
    setLine1(addr.Line1 ?? '');
    setLine2(addr.Line2 ?? '');
    setLine3(addr.Line3 ?? '');
    setLine4(addr.Line4 ?? '');
    setLine5(addr.Line5 ?? '');
    setReason(addr.Reason ?? '');
    setErrors({});
  }

  function clearFields(): void {
    setLine1('');
    setLine2('');
    setLine3('');
    setLine4('');
    setLine5('');
    setReason('');
    setErrors({});
  }

  async function loadAddress(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    setLoading(true);
    setLoaded(false);
    setError('');
    clearFields();
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/postal-address', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Could not load the postal address (${response.status}).`,
        );
        return;
      }
      const addr = data as unknown as PostalAddress;
      if (addr.Found) fillFrom(addr);
      setLoaded(true);
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    // Clear any prior action outcome so a stale confirmation/error from a
    // previous account doesn't linger on the newly-selected one (issue #138).
    setUpdateError('');
    setUpdateOk(false);
    setDeleteError('');
    setDeleteOk(false);
    void loadAddress(value);
  }

  function validateAll(): Record<string, string> {
    const e: Record<string, string> = {};
    const e1 = validateLine('Address Line 1', line1, true);
    if (e1) e.line1 = e1;
    const e2 = validateLine('Address Line 2', line2, true);
    if (e2) e.line2 = e2;
    const e3 = validateLine('Address Line 3', line3, false);
    if (e3) e.line3 = e3;
    const e4 = validateLine('Address Line 4', line4, false);
    if (e4) e.line4 = e4;
    const e5 = validateLine('Address Line 5', line5, false);
    if (e5) e.line5 = e5;
    if (!reason) e.reason = 'Reason for postal address is required.';
    return e;
  }

  async function updateDetails(): Promise<void> {
    const e = validateAll();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    setUpdateError('');
    setUpdateOk(false);
    // Issue #138 — clear any prior delete outcome so the inline region shows
    // only the most-recent action's result.
    setDeleteError('');
    setDeleteOk(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/postal-address-update', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          line1,
          line2,
          ...(line3 ? { line3 } : {}),
          ...(line4 ? { line4 } : {}),
          ...(line5 ? { line5 } : {}),
          reason: reason as
            | 'Fair Deal Scheme'
            | 'Customer Request'
            | 'Internal Process',
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #104: surface the failure as a PERSISTENT inline error next to
        // the action buttons (the toast alone auto-dismisses and is easy to
        // miss). Show the gateway's own Code + Message verbatim — e.g.
        // "1450 — Validation of Input Payload has failed" — so the agent can
        // act on the real reason and retry. `apiError` carries the BFF
        // `gateway_error` body on a true 4xx; `data` carries the
        // SuccessErrorInd:'N' result on a 200.
        //
        // Issue #248 — the RBAC *action* denial (`requirePermission` refuses a
        // blocked action, SCRN-23 `updatePostalAddress`, with 403
        // `{ error: 'forbidden' }` — e.g. a restricted role attempting this
        // write on an OFFLINE journey) shows @dylan219's authoritative verbatim
        // copy inline beside the buttons — never a raw 403 or a generic error.
        // This is distinct from the gateway business-decline (#104) and from the
        // Teller-ID gate (`invalid_teller_id`), which keeps its own message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatPostalUpdateError(apiError ?? data));
        setUpdateError(message);
        toast.error(message);
        return;
      }
      setUpdateOk(true);
      toast.success('Postal address updated successfully.');
      await loadAddress(selectedAccount);
    } catch {
      const message = 'Could not update the postal address. Please retry.';
      setUpdateError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteDetails(): Promise<void> {
    setSubmitting(true);
    // Issue #138 — clear any prior delete (and update) outcome before the call
    // so the inline region reflects only this attempt.
    setDeleteError('');
    setDeleteOk(false);
    setUpdateError('');
    setUpdateOk(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/postal-address-delete', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber: selectedAccount },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #138: surface the failure as a PERSISTENT inline error beside
        // the Delete button (the toast alone auto-dismisses and is easy to
        // miss). Show the gateway's own Code + Message verbatim so the agent
        // can act on the real reason and retry. `apiError` carries the BFF
        // `gateway_error` body on a true 4xx; `data` carries the
        // SuccessErrorInd:'N' result on a 200.
        //
        // Issue #248 — as for the update path, an RBAC *action* denial
        // (`403 { error: 'forbidden' }` from `requirePermission`) shows
        // @dylan219's authoritative verbatim copy inline beside the Delete
        // button; a Teller-ID 403 (`invalid_teller_id`) keeps its own message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatPostalDeleteError(apiError ?? data));
        setDeleteError(message);
        toast.error(message);
        return;
      }
      // On successful deletion the fields are emptied + ready for re-entry, and
      // a definite confirmation shows inline beside the Delete button (#138).
      clearFields();
      setDeleteOk(true);
      toast.success('Postal address deleted successfully.');
    } catch {
      const message = 'Could not delete the postal address. Please retry.';
      setDeleteError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="postal-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            // Postal address is reachable from multiple screens (issue #105):
            // return to the previous screen with its prior state/options
            // intact. Fall back to the dashboard on a deep-link/direct load
            // where there is no in-app history to go back to.
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/dashboard');
            }
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back
        </a>

        {/* Email templates (#759): the EDS Data Breach draft is surfaced here as
            a top-right control (Drive "SCRN 23, 24 & 39"), matching the
            Personal Details (SCRN-24) and Statements (SCRN-39) screens. This
            list holds exactly one template, so the shared EmailTemplateMenu
            renders it as a single button, not a dropdown (#803). Picking
            it pops the source template prefilled best-effort from the in-context
            customer CIF, delivered as a downloadable Outlook draft (`.eml`, #434).
            Nothing is sent from the app: the agent completes + sends the draft to
            the Data Protection Team in Outlook. Email templates are permission-
            free (#710), so this is not RBAC-gated. */}
        <EmailTemplateMenu
          templates={POSTAL_ADDRESS_TEMPLATES}
          context={{ cif: customer.cif ?? '', content: contentMap }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="postal-heading"
          className="text-[28px] font-['Arial',sans-serif] text-[#1d252c] mb-8"
        >
          <span className="font-bold">Personal details: </span>
          <span>View, amend and delete postal address</span>
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Account selector — non-mortgage accounts only (CCF-30/31). Label is
          the shared `<product name> <last 4>` convention (issue #802 —
          @philipc39: "you do this in other screens so same logic"); the
          account NUMBER stays last-4 only (CCF-80, #130/#131 won't-fix). */}
      <div className="mb-6 w-[400px]">
        <Dropdown
          id="postal-account"
          label="Select account to view"
          placeholder="Select"
          value={selectedAccount}
          onChange={onAccountChange}
          options={nonMortgageAccounts.map((a) => ({
            value: a.AccountNumber,
            label: accountOptionLabel(a),
          }))}
        />
      </div>

      {selectedAccount && (
        <>
          {loading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
            >
              Loading postal address…
            </p>
          ) : loaded ? (
            <div className="max-w-[700px] p-8 border-2 border-[#dad7d3] bg-white">
              {/* Issue #399 — VIEW-ONLY notice. When the agent cannot amend the
                  postal address in the current interaction state (e.g. a
                  restricted role on an OFFLINE journey — offline account
                  mutations are denied, #248), the fields render read-only and
                  the blocked action is suppressed; a clear, non-alarming
                  explanation is shown up-front instead of letting them type and
                  fail at submit. Roles that MAY write offline (the CCF-31 VCL
                  exception — ECLAgent — plus the full-offline supervisory roles,
                  #285) and any role online keep the editable path unchanged.
                  Issue #735: when the block is ROLE-level (the update is denied
                  in every state — ServiceAgent, view+delete only), the notice
                  carries @dylan219's verbatim permission copy instead of the
                  #399 online-interaction hint; the still-permitted Delete action
                  stays available (it is gated independently below).
                  Issue #779 (@dylan219, 2026-09-09): the ROLE-level permission
                  notice ("Sorry, you do not have permission to perform this
                  action.") is NOT shown here — the edit/amend fields are already
                  greyed out (read-only) and the Update button suppressed, so the
                  message served no purpose. Only the #399 STATE-only offline
                  hint (which is actionable — going online enables editing)
                  remains. The recoverable inline error beside the button on a
                  live 403 (#80/#82/#248) is unaffected. */}
              {!canUpdate && !updateBlockedByRole && (
                <div
                  id="postal-view-only-notice"
                  role="status"
                  className="mb-6 p-4 bg-[#f3f4f6] border border-[#dad7d3] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  View only — updating the postal address requires an online
                  interaction.
                </div>
              )}
              <div className="space-y-1 mb-6 max-w-[500px]">
                <Field
                  id="postal-line-1"
                  label="Address Line 1"
                  value={line1}
                  onChange={(v) => {
                    setLine1(v);
                    setErrors((e) => ({ ...e, line1: '' }));
                  }}
                  error={errors.line1}
                  readOnly={!canUpdate}
                />
                <Field
                  id="postal-line-2"
                  label="Address Line 2"
                  value={line2}
                  onChange={(v) => {
                    setLine2(v);
                    setErrors((e) => ({ ...e, line2: '' }));
                  }}
                  error={errors.line2}
                  readOnly={!canUpdate}
                />
                <Field
                  id="postal-line-3"
                  label="Address Line 3"
                  value={line3}
                  onChange={(v) => {
                    setLine3(v);
                    setErrors((e) => ({ ...e, line3: '' }));
                  }}
                  error={errors.line3}
                  readOnly={!canUpdate}
                />
                <Field
                  id="postal-line-4"
                  label="Address Line 4"
                  value={line4}
                  onChange={(v) => {
                    setLine4(v);
                    setErrors((e) => ({ ...e, line4: '' }));
                  }}
                  error={errors.line4}
                  readOnly={!canUpdate}
                />
                <Field
                  id="postal-line-5"
                  label="Address Line 5"
                  value={line5}
                  onChange={(v) => {
                    setLine5(v);
                    setErrors((e) => ({ ...e, line5: '' }));
                  }}
                  error={errors.line5}
                  readOnly={!canUpdate}
                />
                <Dropdown
                  id="postal-reason"
                  label="Reason for postal address"
                  placeholder="Select reason"
                  value={reason}
                  onChange={(v) => {
                    setReason(v);
                    setErrors((e) => ({ ...e, reason: '' }));
                  }}
                  options={REASONS.map((r) => ({ value: r, label: r }))}
                  disabled={!canUpdate}
                />
                <div className="min-h-[18px]">
                  {errors.reason && (
                    <p
                      className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                      role="alert"
                    >
                      {errors.reason}
                    </p>
                  )}
                </div>
              </div>

              {/* Issue #399 — an action is suppressed when the agent cannot
                  perform that write in the current state, so they aren't tempted
                  into a submit the server would refuse with a 403. It returns
                  the moment the agent is in a state where the write is
                  permitted. Issue #735: each button is gated on its OWN grant —
                  a ServiceAgent (Online) lacks `updatePostalAddress` but keeps
                  `deletePostalAddress`, so only Update is suppressed for them
                  (the verbatim permission copy shows in the notice above). */}
              {(canUpdate || canDelete) && (
                <div className="flex gap-4">
                  {/* Delete details (CCF-30). */}
                  {canDelete && (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void deleteDetails()}
                      className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Delete details
                    </button>
                  )}
                  {/* Update details — add/amend (CCF-31). */}
                  {canUpdate && (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void updateDetails()}
                      className="px-6 py-3 bg-white text-[#FF703E] border-2 border-[#FF703E] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#fff5f2] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Working…' : 'Update details'}
                    </button>
                  )}
                </div>
              )}

              {/* Issue #104 — persistent inline outcome beside the buttons.
                  A failed update shows the gateway's Code + Message verbatim
                  (recoverable — correct + retry); a success shows a definite
                  confirmation (#82). */}
              {updateError && (
                <div
                  id="postal-update-error"
                  role="alert"
                  aria-live="assertive"
                  className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {updateError}
                </div>
              )}
              {updateOk && (
                <div
                  id="postal-update-success"
                  role="status"
                  aria-live="polite"
                  className="max-w-[640px] p-4 bg-white border border-[#1f8a4c] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  Postal address updated successfully.
                </div>
              )}

              {/* Issue #138 — persistent inline outcome for the Delete action,
                  beside the Delete button (a toast alone auto-dismisses and is
                  easy to miss). A failed delete shows the gateway's Code +
                  Message verbatim (recoverable — retry); a success shows a
                  definite confirmation. */}
              {deleteError && (
                <div
                  id="postal-delete-error"
                  role="alert"
                  aria-live="assertive"
                  className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {deleteError}
                </div>
              )}
              {deleteOk && (
                <div
                  id="postal-delete-success"
                  role="status"
                  aria-live="polite"
                  className="max-w-[640px] p-4 bg-white border border-[#1f8a4c] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  Postal address deleted successfully.
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </main>
  );
}
